import '../index.less';

import { Divider } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useLifecycles } from 'react-use';

import CustomerFormModal from '../../../components/CustomerFormModal';
import InfoGrid from '../../../components/InfoGrid';
import { getApplicationCreditRecordLocalThunk } from '../../../services/applicationService/applicationThunk';
import { AppDispatch, RootState } from '../../../store/store';
import { displayDate } from '../../../utils/datetime.util';
import { getNricInfo } from '../../../utils/nric.util';
import { getAccountDetailsThunk } from '../../../services/collectionService/collectionThunk';
import { getRaceListThunk } from '../../../services/dropdownParameterService/dropdownParameterThunk';
import { useEffect, useState } from 'react';
import { LANGUAGE } from '../../../features/config/configSlice';
import { trimPhoneNo } from '../../../utils/number.util';
import { AccountEnquiryResponse } from '../../../features/collection/collectionSlice';

type CustomerDetailsProps = {
  accountEnquiry: AccountEnquiryResponse;
};

const CustomerDetails: React.FC<CustomerDetailsProps> = (props) => {
  const { accountEnquiry } = props;
  const dispatch = useDispatch<AppDispatch>();
  const { raceState } = useSelector((state: RootState) => state.dropdownParameter);
  const { language } = useSelector((state: RootState) => state.config);

  const [creditRecords, setCreditRecords] = useState<any>({});
  const [application, setApplication] = useState<any>({});

  const editable = true;

  const customerInformation = [
    {
      title: 'Full Name',
      value: accountEnquiry?.accountDetails?.applicantFirstName
        ? accountEnquiry?.accountDetails?.applicantFirstName
        : application?.user?.firstName,
    },
    {
      title: 'Customer Type',
      value: application?.customerType ?? 'NTU',
    },
    {
      title: 'Date of Birth',
      value: accountEnquiry?.accountDetails?.applicantNric
        ? displayDate(
            getNricInfo(
              application?.user?.nric ?? accountEnquiry?.accountDetails?.applicantNric ?? ''
            ).birthDate.toDate()
          )
        : '-',
    },
    {
      title: 'Race',
      value:
        (language === LANGUAGE.EN
          ? raceState.data.find((item) => application?.user?.raceId == item.id)?.name?.toUpperCase()
          : raceState.data
              .find((item) => application?.user?.raceId == item.id)
              ?.malayName?.toUpperCase()) ??
        accountEnquiry?.accountDetails?.applicantRace ??
        application?.applicantRace,
    },
    {
      title: 'Identity No',
      value:
        application?.user?.nric ??
        accountEnquiry?.accountDetails?.applicantNric ??
        application?.applicantNric,
    },
    {
      title: 'Phone No',
      value: trimPhoneNo(
        accountEnquiry?.accountDetails?.applicantPhoneNo ??
          application?.user?.phoneNo ??
          application?.applicantPhoneNo
      ),
    },
    {
      title: 'Email Address',
      value:
        application?.user?.email ??
        accountEnquiry?.accountDetails?.applicantEmail ??
        application?.applicantEmail,
    },
    {
      title: 'IC Type',
      value: application?.user?.icType,
    },
    {
      title: 'Residential Address',
      value: [
        accountEnquiry?.accountDetails?.applicantAddress1 ??
          application?.applicantAddress1 ??
          application?.user?.residentialAddress1,

        accountEnquiry?.accountDetails?.applicantAddress2 ??
          application?.applicantAddress2 ??
          application?.user?.residentialAddress2,

        accountEnquiry?.accountDetails?.applicantAddress3 ??
          application?.applicantAddress3 ??
          application?.user?.residentialAddress3,
      ]
        .filter((el) => el)
        .join(', '),
    },
    {
      title: 'Postcode',
      value:
        accountEnquiry?.accountDetails?.applicantPostcode ??
        application?.applicantPostcode ??
        application?.user?.postcode,
    },
    {
      title: 'City',
      value:
        accountEnquiry?.accountDetails?.applicantCity ??
        application?.applicantCity ??
        application?.user?.city,
    },
    {
      title: 'State',
      value:
        accountEnquiry?.accountDetails?.applicantState ??
        application?.applicantState ??
        application?.user?.state,
    },
    {
      title: '',
      value: '',
    },
    {
      title: 'Risk Profiling',
      value: creditRecords?.amla === 'APPROVE' ? 'LOW' : 'MEDIUM/HIGH',
    },
    {
      title: 'Marketing Consent',
      value:
        application?.user?.agreeMarketing ?? application?.applicantAcceptMarketing ? 'YES' : 'NO',
    },
    {
      title: 'i-Score',
      value:
        application?.iScore && application?.iScore?.length > 0
          ? application?.iScore[0].iScore
          : '-',
    },
    {
      title: 'Tin Validated Number',
      value: application?.user?.tinNumber ?? '-',
    },
  ];

  const fetchApplicationCreditRecord = (applicationId: string) => {
    dispatch(getApplicationCreditRecordLocalThunk({ applicationId: applicationId }))
      .unwrap()
      .then((res) => {
        setCreditRecords(res);
      })
      .catch((error) => {
        console.error('[fetchApplicationCreditRecord] Failed to fetch credit records:', error);
      });
  };

  useLifecycles(
    () =>
      accountEnquiry?.agreementDetails?.applicationId &&
      fetchApplicationCreditRecord(accountEnquiry?.agreementDetails?.applicationId)
  );

  useEffect(() => {
    dispatch(getRaceListThunk());
  }, [dispatch]);

  useEffect(() => {
    setApplication(accountEnquiry?.agreementDetails?.application);
  }, [accountEnquiry?.agreementDetails?.application]);

  return (
    <div className="account-enquiry-container">
      <div className="account-enquiry-section-container">
        <div className="flex justify-between items-end">
          <h3 className="account-enquiry-section-subtitle mb-0">Customer Information</h3>
          {editable && (
            <CustomerFormModal
              key={application?.id}
              applicationDetails={application}
              callback={async () => {
                accountEnquiry?.agreementDetails?.applicationId &&
                  fetchApplicationCreditRecord(accountEnquiry?.agreementDetails?.applicationId);

                await dispatch(
                  getAccountDetailsThunk({
                    agreementNo: accountEnquiry?.agreementDetails?.agreementNo,
                  })
                );
              }}
              from="AccountEnquiry"
            />
          )}
        </div>

        <Divider style={{ margin: 0 }} />
        <div className="info-container">
          <InfoGrid data={customerInformation} />
        </div>
      </div>
    </div>
  );
};

export default CustomerDetails;
